import React from "react";
import PinField from "react-pin-field";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { getRoomByHashedId } from "../../../api/rooms/getRoomByHashedId";
import { useFullpageLoading } from "../../../hooks/useFullPageLoading";
import { useParams } from "react-router-dom";

const Wrapper = styled("div")`
  display: flex;
  flex-flow: column;
  row-gap: 24px;
  padding: 20px 14px;
`;

const PinFieldWrapper = styled("div")`
  display: flex;
  flex-flow: column;
  row-gap: 4px;
`;

const PinFieldInputs = styled("div")`
  display: flex;
  column-gap: 8px;
  justify-content: center;

  .code-input {
    width: 100%;
    border: 2px solid #dcdddd;
    height: 56px;
    padding: 8px;
    display: flex;
    max-width: 56px;
    box-sizing: border-box;
    transition: border-color 150ms;
    border-radius: 8px;
    background-color: #fff;

    font-size: 1.25rem;
    text-align: center;
    font-family: "Versos", sans-serif;

    &:focus {
      border-color: ${(props) => props.theme.colors.primary};
      outline: transparent;
    }
    &[data-error="true"] {
      border-color: #ff2e46;
    }
  }
`;

const PinFieldError = styled("span")`
  color: #ff2e46;
  text-align: center;

  font-size: 0.825rem;
  font-family: "Versos", sans-serif;

  &[data-show="false"] {
    visibility: hidden;
  }
`;

const Button = styled("button")`
  width: 100%;
  color: #fff;
  height: 56px;
  position: relative;
  border: 2px solid ${(props) => props.theme.colors.primary};
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.primary};

  font-size: 1rem;
  font-family: "Versos", sans-serif;
  font-weight: 600;

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(60, 60, 60, 0.2);
    opacity: 0;
    transition: opacity 0.15s;
    pointer-events: none;
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors.primary};
    outline-offset: 2px;
  }
  &:hover:not(:disabled) {
    cursor: pointer;
  }
  &:disabled {
    opacity: 0.5;
  }
  &:active:not(:disabled) {
    .overlay {
      opacity: 1;
    }
  }
`;

export const CodeInput = (): JSX.Element => {
  const [accessCode, setAccessCode] = React.useState<string>("");
  const [hasError, setHasError] = React.useState<boolean>(false);
  const pinRef = React.useRef<(HTMLInputElement)[]>([]);
  const { code: urlCode } = useParams();

  const navigate = useNavigate();

  const firstTry = React.useRef<boolean>(true);

  const { handleShowFullPageLoading } = useFullpageLoading();

  const submitCode = async (code?: string) => {
    if (accessCode.length < 6 && !code) return;

    handleShowFullPageLoading(true);

    try {
      const roomToken = await getRoomByHashedId(
        code ?? accessCode
      );

      navigate(`/?token=${roomToken.token}`, {
        replace: true,
      });
    } catch (error) {
      setHasError(true);
    } finally {
      handleShowFullPageLoading(false);
    }
  };

  const handlePinFieldChange = React.useCallback((value: string) => {
    setAccessCode(value);
    setHasError(false);
  }, []);

  React.useEffect(() => {
    if (urlCode && urlCode.length === 6) {
      urlCode.split("").forEach((char, index) => {
        if (pinRef.current[index]) {
          pinRef.current[index]!.value = char;
        }
      });
      handlePinFieldChange(urlCode);
      submitCode(urlCode);
    }
  }, [urlCode]);

  return (
    <Wrapper>
      <PinFieldWrapper>
        <PinFieldInputs>
          <PinField
            autoFocus
            name="code"
            type="text"
            length={6}
            validate={/^[0-9a-zA-Z]$/}
            onChange={handlePinFieldChange}
            inputMode="text"
            className="code-input"
            placeholder="_"
            ref={pinRef}
            data-error={hasError}
            onComplete={(value) => {
              if (!firstTry.current) return;

              firstTry.current = false;
              submitCode(value);
            }}
          />
        </PinFieldInputs>

        <PinFieldError data-show={hasError}>
          Código inválido. Verifique e insira novamente
        </PinFieldError>
      </PinFieldWrapper>

      <Button disabled={accessCode?.length < 6} onClick={() => submitCode()}>
        ACESSAR
        <span className="overlay" />
      </Button>
    </Wrapper>
  );
};
